import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Knowledge Base performance metrics display component
 */
const KnowledgeBaseMetricsPanel = ({
    knowledgeBase,
    isOpen,
    onClose
}) => {
    const { getToken } = useAuth();
    const { showToast } = useToast();
    const { theme } = useTheme();

    const [loading, setLoading] = useState(false);
    const [metrics, setMetrics] = useState(null);
    const [timeRange, setTimeRange] = useState('30d');
    const [refreshing, setRefreshing] = useState(false);

    /**
     * Load Knowledge Base metrics
     */
    const loadMetrics = async () => {
        try {
            setLoading(true);
            const token = await getToken();

            const response = await apiRequest(`/api/admin/knowledge-base/${knowledgeBase.id}/metrics?timeRange=${timeRange}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorMessage = await parseApiError(response);
                throw new ApiError(errorMessage, response.status, response);
            }

            const data = await response.json();
            setMetrics(data.metrics);
        } catch (error) {
            console.error('Error loading metrics:', error);
            showToast('Failed to load performance metrics', 'error');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    /**
     * Refresh metrics
     */
    const handleRefresh = async () => {
        setRefreshing(true);
        await loadMetrics();
    };

    /**
     * Handle time range change
     */
    const handleTimeRangeChange = (newRange) => {
        setTimeRange(newRange);
    };

    /**
     * Format number for display
     */
    const formatNumber = (num) => {
        if (num === null || num === undefined) return 'N/A';
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    /**
     * Format percentage for display
     */
    const formatPercentage = (num) => {
        if (num === null || num === undefined) return 'N/A';
        return `${(num * 100).toFixed(1)}%`;
    };

    /**
     * Format duration for display
     */
    const formatDuration = (ms) => {
        if (ms === null || ms === undefined) return 'N/A';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    /**
     * Get metric trend indicator
     */
    const getTrendIndicator = (current, previous) => {
        if (!current || !previous) return null;

        const change = ((current - previous) / previous) * 100;
        if (Math.abs(change) < 1) return null;

        const isPositive = change > 0;
        const color = isPositive ? 'text-green-400' : 'text-red-400';
        const arrow = isPositive ? '↗' : '↘';

        return (
            <span className={`text-xs ${color} ml-2`}>
                {arrow} {Math.abs(change).toFixed(1)}%
            </span>
        );
    };

    /**
     * Render metric card
     */
    const MetricCard = ({ title, value, subtitle, trend, icon, color = 'text-teal-400' }) => (
        <div className={`rounded-lg p-4 ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
            <div className="flex items-center justify-between mb-2">
                <h4 className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>{title}</h4>
                <span className={`text-lg ${color}`}>{icon}</span>
            </div>
            <div className="flex items-baseline">
                <span className={`text-2xl font-bold ${color}`}>{value}</span>
                {trend}
            </div>
            {subtitle && (
                <p className={`text-xs mt-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>{subtitle}</p>
            )}
        </div>
    );

    /**
     * Render usage chart (simplified bar chart)
     */
    const UsageChart = ({ data, title }) => {
        if (!data || data.length === 0) return null;

        const maxValue = Math.max(...data.map(d => d.value));

        return (
            <div className={`rounded-lg p-4 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
            }`}>
                <h4 className={`text-sm font-medium mb-4 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>{title}</h4>
                <div className="space-y-2">
                    {data.map((item, index) => (
                        <div key={index} className="flex items-center">
                            <div className={`w-16 text-xs truncate ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>{item.label}</div>
                            <div className="flex-1 mx-2">
                                <div className={`rounded-full h-2 ${
                                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
                                }`}>
                                    <div
                                        className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${(item.value / maxValue) * 100}%` }}
                                    />
                                </div>
                            </div>
                            <div className={`w-12 text-xs text-right ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>{item.value}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Load metrics when component opens or time range changes
    useEffect(() => {
        if (isOpen && knowledgeBase?.id) {
            loadMetrics();
        }
    }, [isOpen, knowledgeBase?.id, timeRange]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`rounded-lg border w-full max-w-6xl max-h-[95vh] overflow-hidden ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                {/* Header */}
                <div className={`px-6 py-4 border-b flex justify-between items-center ${
                    theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div>
                        <h3 className={`text-lg font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>Performance Metrics</h3>
                        <p className={`text-sm mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            {knowledgeBase.name} • Last {timeRange === '7d' ? '7 days' : timeRange === '30d' ? '30 days' : '90 days'}
                        </p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <select
                            value={timeRange}
                            onChange={(e) => handleTimeRangeChange(e.target.value)}
                            className={`px-3 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                        </select>
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors disabled:opacity-50 dark:text-white"
                        >
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                        <button
                            onClick={onClose}
                            className={`transition-colors ${
                                theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(95vh-120px)]">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400"></div>
                            <span className={`ml-3 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>Loading metrics...</span>
                        </div>
                    ) : !metrics ? (
                        <div className="text-center py-12">
                            <div className="text-4xl mb-4">📊</div>
                            <h3 className={`text-lg font-medium mb-2 ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>No metrics available</h3>
                            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                Metrics will appear here once this Knowledge Base file starts being used.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Overview Metrics */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <MetricCard
                                    title="Total Queries"
                                    value={formatNumber(metrics.total_queries)}
                                    subtitle="Queries processed"
                                    trend={getTrendIndicator(metrics.total_queries, metrics.previous_total_queries)}
                                    icon="🔍"
                                    color="text-blue-400"
                                />
                                <MetricCard
                                    title="Hit Rate"
                                    value={formatPercentage(metrics.hit_rate)}
                                    subtitle="Successful retrievals"
                                    trend={getTrendIndicator(metrics.hit_rate, metrics.previous_hit_rate)}
                                    icon="🎯"
                                    color="text-green-400"
                                />
                                <MetricCard
                                    title="Avg Response Time"
                                    value={formatDuration(metrics.avg_response_time)}
                                    subtitle="Query processing time"
                                    trend={getTrendIndicator(metrics.avg_response_time, metrics.previous_avg_response_time)}
                                    icon="⚡"
                                    color="text-yellow-400"
                                />
                                <MetricCard
                                    title="Relevance Score"
                                    value={metrics.avg_relevance_score ? metrics.avg_relevance_score.toFixed(2) : 'N/A'}
                                    subtitle="Average relevance"
                                    trend={getTrendIndicator(metrics.avg_relevance_score, metrics.previous_avg_relevance_score)}
                                    icon="⭐"
                                    color="text-purple-400"
                                />
                            </div>

                            {/* Usage Patterns */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {metrics.daily_usage && (
                                    <UsageChart
                                        data={metrics.daily_usage}
                                        title="Daily Usage Pattern"
                                    />
                                )}

                                {metrics.top_queries && (
                                    <div className={`rounded-lg p-4 ${
                                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                    }`}>
                                        <h4 className={`text-sm font-medium mb-4 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>Top Query Types</h4>
                                        <div className="space-y-3">
                                            {metrics.top_queries.slice(0, 5).map((query, index) => (
                                                <div key={index} className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <p className={`text-sm truncate ${
                                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                        }`}>{query.query}</p>
                                                        <p className={`text-xs ${
                                                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                                        }`}>
                                                            {query.count} queries • {formatPercentage(query.success_rate)} success
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className="text-teal-400 text-sm">{query.count}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Performance Details */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                <div className={`rounded-lg p-4 ${
                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                }`}>
                                    <h4 className={`text-sm font-medium mb-3 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Chunk Performance</h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Total Chunks</span>
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>{metrics.total_chunks || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Active Chunks</span>
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>{metrics.active_chunks || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Avg Chunk Size</span>
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>{metrics.avg_chunk_size ? `${metrics.avg_chunk_size} chars` : 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={`rounded-lg p-4 ${
                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                }`}>
                                    <h4 className={`text-sm font-medium mb-3 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Quality Metrics</h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>High Relevance</span>
                                            <span className="text-green-400 text-sm">{formatPercentage(metrics.high_relevance_rate)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Medium Relevance</span>
                                            <span className="text-yellow-400 text-sm">{formatPercentage(metrics.medium_relevance_rate)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Low Relevance</span>
                                            <span className="text-red-400 text-sm">{formatPercentage(metrics.low_relevance_rate)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={`rounded-lg p-4 ${
                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                }`}>
                                    <h4 className={`text-sm font-medium mb-3 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Usage Stats</h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Peak Hour</span>
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>{metrics.peak_hour || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Last Used</span>
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {metrics.last_used ? new Date(metrics.last_used).toLocaleDateString() : 'Never'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>Unique Users</span>
                                            <span className={`text-sm ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>{metrics.unique_users || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Associated Parrots Performance */}
                            {metrics.parrot_performance && metrics.parrot_performance.length > 0 && (
                                <div className={`rounded-lg p-4 ${
                                    theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                }`}>
                                    <h4 className={`text-sm font-medium mb-4 ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Performance by Associated Parrot</h4>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className={`border-b ${
                                                    theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
                                                }`}>
                                                    <th className={`text-left pb-2 ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>Parrot</th>
                                                    <th className={`text-right pb-2 ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>Queries</th>
                                                    <th className={`text-right pb-2 ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>Hit Rate</th>
                                                    <th className={`text-right pb-2 ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>Avg Response</th>
                                                    <th className={`text-right pb-2 ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>Relevance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {metrics.parrot_performance.map((parrot, index) => (
                                                    <tr key={index} className={`border-b ${
                                                        theme === 'dark' ? 'border-gray-600/50' : 'border-gray-200/50'
                                                    }`}>
                                                        <td className={`py-2 ${
                                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                        }`}>{parrot.name}</td>
                                                        <td className={`py-2 text-right ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>{parrot.queries}</td>
                                                        <td className={`py-2 text-right ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>{formatPercentage(parrot.hit_rate)}</td>
                                                        <td className={`py-2 text-right ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>{formatDuration(parrot.avg_response_time)}</td>
                                                        <td className={`py-2 text-right ${
                                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                                        }`}>{parrot.avg_relevance?.toFixed(2) || 'N/A'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={`px-6 py-4 border-t flex justify-end ${
                    theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                    <button
                        onClick={onClose}
                        className={`px-4 py-2 rounded-lg transition-colors ${
                            theme === 'dark'
                                ? 'bg-gray-600 text-white hover:bg-gray-700'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default KnowledgeBaseMetricsPanel;
